import { useState } from 'react'
import type { User } from '../../../utils/loginUtils'
import { getMessage } from '../../../utils/messageUtils'
import FilterBar, { type filter } from '../FilterBar'
import UsersListEntry from './UsersListEntry'
import Popup, { type ExtraButton } from '../../PopupForm/Popup'
import UserPopupContent from './UserPopupContent'

interface UsersListProps {
  currentUser: User
  users: User[]
  isMobile: boolean
  setLoadData: React.Dispatch<React.SetStateAction<boolean>>
}

function UsersList({
  currentUser,
  users,
  isMobile,
  setLoadData,
}: UsersListProps) {
  // Filters
  const [nameFilter, setNameFilter] = useState<string>('')
  const [alertFilter, setAlertFilter] = useState<boolean>(false)
  const [adminFilter, setAdminFilter] = useState<boolean>(false)

  // Popup
  const [popupOpen, setPopupOpen] = useState<boolean>(false)
  const [popupMessage, setPopupMessage] = useState<string>('')
  const [popupChildren, setPopupChildren] = useState<React.ReactNode>(null)
  const [popupOnOk, setPopupOnOk] = useState<(() => void) | undefined>(() => {})
  const [popupExtraButtons, setPopupExtraButtons] = useState<
    ExtraButton[] | undefined
  >(undefined)

  const openPopup = (obj: object | null) => {
    const userToEdit = obj as User | null
    const title = userToEdit ? userToEdit.username : getMessage('user_new')
    setPopupMessage(title)
    setPopupChildren(
      <UserPopupContent
        currentUser={currentUser}
        user={userToEdit}
        isMobile={isMobile}
        setPopupOnOk={setPopupOnOk}
        closePopup={() => setPopupOpen(false)}
        reLoadData={() => setLoadData(true)}
        setPopupExtraButtons={setPopupExtraButtons}
      />
    )
    setPopupOpen(true)
  }

  const generateFilterItems = () => {
    return [
      {
        type: 'text',
        placeholder: getMessage('name'),
        value: nameFilter,
        setValue: setNameFilter,
      },
      {
        type: 'checkbox',
        checkboxes: [
          {
            label: getMessage('user_alerts_only'),
            value: alertFilter,
            setValue: setAlertFilter,
          },
          {
            label: getMessage('user_admin_only'),
            value: adminFilter,
            setValue: setAdminFilter,
          },
        ],
      },
      {},
    ] as filter[]
  }

  const applyFilters = (user: User) => {
    return (
      (nameFilter === '' ||
        user.username.toLowerCase().includes(nameFilter.toLowerCase())) &&
      (alertFilter ? user.alerts : true) &&
      (adminFilter ? user.admin : true)
    )
  }

  const generateUsersList = () => {
    return users
      ?.filter(user => applyFilters(user))
      .map(user => {
        return (
          <UsersListEntry
            key={user.username}
            user={user}
            isMobile={isMobile}
            openPopup={openPopup}
          />
        )
      })
  }

  return (
    <div
      className={`user-list-container ${isMobile ? 'user-list-container-mobile' : ''}`}
    >
      <FilterBar
        filters={generateFilterItems()}
        openPopup={openPopup}
        isMobile={isMobile}
      />
      <div className="user-list-body-container">{generateUsersList()}</div>
      <Popup
        isOpen={popupOpen}
        message={popupMessage}
        children={popupChildren}
        isMobile={isMobile}
        extraButtons={popupExtraButtons}
        onOk={popupOnOk}
        onClose={() => setPopupOpen(false)}
      />
      {/* <Spinner isOpen={isLoading} overlay={true} /> */}
    </div>
  )
}

export default UsersList
